import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as RadixSwitch from "@radix-ui/react-switch";

import { Check } from "./icons";
import { cn } from "./cn";

export interface CheckboxProps {
  id?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}

export function Checkbox({
  id,
  checked,
  onCheckedChange,
  label,
  disabled,
}: CheckboxProps) {
  return (
    // The visible box is 24px; the label row carries the 44px tap target.
    <label
      className={cn(
        "flex min-h-11 cursor-pointer items-center gap-3 text-body text-fg",
        disabled && "cursor-not-allowed text-fg-disabled",
      )}
    >
      <RadixCheckbox.Root
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => {
          onCheckedChange?.(next === true);
        }}
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-sm bg-sunken shadow-[inset_0_0_0_1.5px_var(--color-line)]",
          "data-[state=checked]:bg-accent data-[state=checked]:text-on-accent data-[state=checked]:shadow-none",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        )}
      >
        <RadixCheckbox.Indicator>
          <Check size={16} />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      {label}
    </label>
  );
}

export interface SwitchProps {
  id?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}

export function Switch({
  id,
  checked,
  onCheckedChange,
  label,
  disabled,
}: SwitchProps) {
  return (
    <label
      className={cn(
        "flex min-h-11 cursor-pointer items-center justify-between gap-3 text-body text-fg",
        disabled && "cursor-not-allowed text-fg-disabled",
      )}
    >
      {label}
      <RadixSwitch.Root
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className={cn(
          "relative h-[31px] w-[51px] shrink-0 rounded-full bg-line-strong transition-colors motion-fast",
          "data-[state=checked]:bg-accent",
          "disabled:opacity-50",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        )}
      >
        <RadixSwitch.Thumb className="block size-[27px] translate-x-0.5 rounded-full bg-on-accent shadow-card transition-transform motion-fast data-[state=checked]:translate-x-[22px]" />
      </RadixSwitch.Root>
    </label>
  );
}
